import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import { Navigate, type RouteObject } from "react-router-dom";

const NotFound = lazy(() => import("../pages/NotFound"));
const RedesignHome = lazy(() => import("../pages/home/page-redesign"));
// Roadmap, News, and Changelog are temporarily hidden and redirect to the home
// page (see routes below). Their page components are kept for an easy re-add.
// const RoadmapPage = lazy(() => import("../pages/roadmap/page"));
const SupportPage = lazy(() => import("../pages/support/page"));
const FAQPage = lazy(() => import("../pages/faq/page"));
// const NewsPage = lazy(() => import("../pages/news/page"));
// const ChangelogPage = lazy(() => import("../pages/changelog/page"));
const PrivacyPage = lazy(() => import("../pages/privacy/page"));
const TermsPage = lazy(() => import("../pages/terms/page"));
const ArtistTermsPage = lazy(() => import("../pages/artist-terms/page"));
const CookiesPage = lazy(() => import("../pages/cookies/page"));
const LegalNoticePage = lazy(() => import("../pages/legal-notice/page"));
const SubscriptionCancellationPage = lazy(() => import("../pages/subscription-cancellation/page"));
const UnsubscribePage = lazy(() => import("../pages/unsubscribe/page"));
const AdminLoginPage = lazy(() => import("../pages/admin/login"));
const AdminPhasesPage = lazy(() => import("../pages/admin/phases"));
const AdminFeaturesPage = lazy(() => import("../pages/admin/features"));
const AdminFaqsPage = lazy(() => import("../pages/admin/faqs"));
const AdminNewsPage = lazy(() => import("../pages/admin/news"));
const AdminChangelogPage = lazy(() => import("../pages/admin/changelog"));
const AdminSettingsPage = lazy(() => import("../pages/admin/settings"));
const AdminContactsPage = lazy(() => import("../pages/admin/contacts"));
const AdminLegalPage = lazy(() => import("../pages/admin/legal"));
const AdminStoriesPage = lazy(() => import("../pages/admin/stories/page"));
const AdminAuthProvider = lazy(async () => ({
  default: (await import("../components/admin/AdminAuthProvider")).AdminAuthProvider,
}));
const AdminLayout = lazy(() => import("../components/admin/AdminLayout"));

function RouteFallback() {
  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
      <div className="text-center text-white">
        <div className="w-10 h-10 mx-auto mb-4 rounded-full border-2 border-cyan-400/30 border-t-cyan-400 animate-spin" />
        <p className="text-sm text-gray-400">Loading Fezle...</p>
      </div>
    </div>
  );
}

function withSuspense(element: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

function withAdminAuth(element: ReactNode) {
  return withSuspense(<AdminAuthProvider>{element}</AdminAuthProvider>);
}

function withAdminLayout(element: ReactNode) {
  return withSuspense(
    <AdminAuthProvider>
      <AdminLayout>{element}</AdminLayout>
    </AdminAuthProvider>,
  );
}

// Redirect component for /app route - redirects to Fezle chat app
function AppRedirect() {
  // @ts-ignore - __APP_URL__ is defined in vite.config.ts
  const appUrl = __APP_URL__ || "http://localhost:5173";

  useEffect(() => {
    window.location.href = appUrl;
  }, [appUrl]);

  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="text-center text-white">
        <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
          <i className="ri-planet-line text-4xl text-cyan-400"></i>
        </div>
        <h2 className="text-xl font-semibold mb-2">Redirecting to Fezle...</h2>
        <p className="text-gray-400">If you are not redirected, <a href={appUrl} className="text-cyan-400 hover:underline">click here</a>.</p>
      </div>
    </div>
  );
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: withSuspense(<RedesignHome />),
  },
  {
    // Temporarily hidden - redirect to home until the redesigned versions land.
    path: "/roadmap",
    element: <Navigate to="/" replace />,
  },
  {
    path: "/support",
    element: withSuspense(<SupportPage />),
  },
  {
    path: "/faq",
    element: withSuspense(<FAQPage />),
  },
  {
    // Temporarily hidden - redirect to home until the redesigned versions land.
    path: "/news",
    element: <Navigate to="/" replace />,
  },
  {
    // Temporarily hidden - redirect to home until the redesigned versions land.
    path: "/changelog",
    element: <Navigate to="/" replace />,
  },
  {
    path: "/privacy",
    element: withSuspense(<PrivacyPage />),
  },
  {
    path: "/terms",
    element: withSuspense(<TermsPage />),
  },
  {
    path: "/artist-terms",
    element: withSuspense(<ArtistTermsPage />),
  },
  {
    path: "/cookies",
    element: withSuspense(<CookiesPage />),
  },
  {
    path: "/legal-notice",
    element: withSuspense(<LegalNoticePage />),
  },
  {
    path: "/subscription-cancellation",
    element: withSuspense(<SubscriptionCancellationPage />),
  },
  {
    path: "/unsubscribe",
    element: withSuspense(<UnsubscribePage />),
  },
  {
    path: "/app",
    element: <AppRedirect />,
  },
  {
    path: "/admin",
    element: withAdminAuth(<AdminLoginPage />),
  },
  {
    path: "/admin/phases",
    element: withAdminAuth(<AdminPhasesPage />),
  },
  {
    path: "/admin/features",
    element: withAdminAuth(<AdminFeaturesPage />),
  },
  {
    path: "/admin/faqs",
    element: withAdminAuth(<AdminFaqsPage />),
  },
  {
    path: "/admin/news",
    element: withAdminAuth(<AdminNewsPage />),
  },
  {
    path: "/admin/changelog",
    element: withAdminAuth(<AdminChangelogPage />),
  },
  {
    path: "/admin/settings",
    element: withAdminAuth(<AdminSettingsPage />),
  },
  {
    path: "/admin/legal",
    element: withAdminAuth(<AdminLegalPage />),
  },
  {
    path: "/admin/contacts",
    element: withAdminAuth(<AdminContactsPage />),
  },
  {
    path: "/admin/stories",
    element: withAdminLayout(<AdminStoriesPage />),
  },
  {
    path: "/admin/stories/:storyId",
    element: withAdminLayout(<AdminStoriesPage />),
  },
  {
    path: "*",
    element: withSuspense(<NotFound />),
  },
];

export default routes;
